import { hasCapability, type User } from "../base";
import { NON_EXISTENT_NUMERIC_ID } from "../utils/types";
import { projectWhere } from "./project";

export const bid = {
  create: (user: User, buyerOrganizationId: string) => {
    if (hasCapability(user, "bid.create.all")) {
      return true;
    }
    if (hasCapability(user, "bid.create.org")) {
      return user.person.organizationId === buyerOrganizationId;
    }
    return false;
  },
  viewCreateBid: (user: User) => hasCapability(user, "bid.viewCreateBid"),
  read: (user: User) => hasCapability(user, "bid.read"),
  readAnyScope: (user: User) =>
    hasCapability(user, "bid.read.all") || hasCapability(user, "bid.read.org") || hasCapability(user, "bid.read.owned"),
  delete: (user: User) => hasCapability(user, "bid.delete"),
  merge: (user: User) => hasCapability(user, "bid.merge"),
  unmerge: (user: User) => hasCapability(user, "bid.unmerge"),
  readMerged: (user: User) => hasCapability(user, "bid.readMerged"),
  warranty: {
    create: (user: User) => hasCapability(user, "bid.warranty.create"),
    convert: (user: User) => hasCapability(user, "bid.warranty.convert"),
  },
};

type BidScopeClause =
  | { bidProject: { organizations: { some: { organization: { id: string }; deletedAt: null } } } }
  | { workSite: { homeownerId: string } };

export const bidWhereUnique = (user: User) => {
  if (hasCapability(user, "bid.read.all")) {
    return {};
  }

  const clauses: BidScopeClause[] = [];

  if (hasCapability(user, "bid.read.org")) {
    clauses.push({
      bidProject: {
        organizations: {
          some: {
            organization: {
              id: user.person.organizationId,
            },
            deletedAt: null,
          },
        },
      },
    });
  }
  if (hasCapability(user, "bid.read.owned")) {
    clauses.push({
      workSite: {
        homeownerId: user.person.id,
      },
    });
  }

  if (clauses.length === 0) {
    return { AND: [{ id: NON_EXISTENT_NUMERIC_ID }] };
  }
  return { OR: clauses };
};

// Contractors and crews hold no bid.read scope, so they reach the bid behind their own job only
// through a project on it they can read.
export const bidOrProjectWhere = (user: User) => {
  if (hasCapability(user, "bid.read.all")) {
    return {};
  }
  const readableProject = projectWhere(user);
  return {
    OR: [
      bidWhereUnique(user),
      { bidProject: readableProject },
      { childProjects: { some: readableProject } },
      { requestsForQuote: { some: { projects: { some: readableProject } } } },
    ],
  };
};

// List-context counterpart to bidWhereUnique. Deliberately omits the
// bid.read.owned tier — list endpoints (bidDao.findMany) should scope by
// organization, not by homeownerId. A HOMEOWNER fetching their own bid via
// findUnique goes through bidWhereUnique which does honor bid.read.owned.
export const bidOrganizationFilter = (user: User) => {
  if (hasCapability(user, "bid.read.all")) {
    return {};
  }

  const clauses: { bidProject: { organizations: { some: { organizationId: string; deletedAt: null } } } }[] = [];

  if (hasCapability(user, "bid.read.org")) {
    clauses.push({
      bidProject: {
        organizations: {
          some: {
            organizationId: user.person.organizationId,
            deletedAt: null,
          },
        },
      },
    });
  }

  if (clauses.length === 0) {
    return { AND: [{ id: NON_EXISTENT_NUMERIC_ID }] };
  }
  return { OR: clauses };
};
